// Story 2.2 — Admin portfolio detail at /admin/portfolios/:portfolio_id.
// Story 2.3 — Section 2 now hosts the inline release form (when no
// drop yet) OR the live-drop summary (when a drop exists). Reuses
// <DropReleaseForm> from features/admin/drops/ so the wizard +
// detail-page panel share JSX.

import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@greenhold/components";

import {
  useAdminPortfolioDetail,
  type AdminPortfolioCreatorRow,
} from "../features/admin/portfolios/useAdminPortfolioDetail";
import type { TierDistribution } from "../features/admin/portfolios/useAdminPortfolios";
import { useAdminApprovedCreators } from "../features/admin/portfolios/useAdminApprovedCreators";
import { useAssignCreatorToPortfolio } from "../features/admin/portfolios/useAssignCreatorToPortfolio";
import { useCloseRecruiting } from "../features/admin/portfolios/useCloseRecruiting";
import { useReopenPortfolio } from "../features/admin/portfolios/useReopenPortfolio";
import {
  useAdminDropForPortfolio,
  type AdminDropDetail,
} from "../features/admin/drops/useAdminDropForPortfolio";
import {
  DropReleaseForm,
  EMPTY_RELEASE_FORM_STATE,
  formatEur,
  isFormValid as isReleaseFormValid,
  parseDatetimeLocalToIso,
  type DropKind,
  type DropReleaseFormState,
} from "../features/admin/drops/DropReleaseForm";
import {
  AdminDropReleaseError,
  useAdminCreateDrop,
  type ReleaseDropRequest,
} from "../features/admin/drops/useAdminCreateDrop";

export function AdminPortfolioDetailPage() {
  const { portfolio_id } = useParams<{ portfolio_id: string }>();
  const query = useAdminPortfolioDetail(portfolio_id ?? null);
  const dropQuery = useAdminDropForPortfolio(portfolio_id ?? null);

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-wider font-mono opacity-60 mb-2">
          <Link to="/admin/portfolios" className="underline decoration-dotted">
            ← All portfolios
          </Link>
        </p>

        {query.isPending ? (
          <>
            <Skeleton className="h-10 w-64 mb-3" />
            <Skeleton className="h-4 w-96 mb-8" />
            <Skeleton className="h-72 w-full rounded-lg" />
          </>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {query.error.message === "PORTFOLIO_NOT_FOUND"
                  ? "Portfolio not found."
                  : `Couldn't load portfolio: ${query.error.message}`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-3">
                {query.data!.data.portfolio.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge variant="secondary">
                  {query.data!.data.portfolio.kind}
                </Badge>
                <Badge
                  variant={
                    query.data!.data.portfolio.recruiting_status === "composed"
                      ? "success"
                      : query.data!.data.portfolio.recruiting_status ===
                          "recruiting"
                        ? "warning"
                        : query.data!.data.portfolio.recruiting_status ===
                            "stalled"
                          ? "destructive"
                          : "secondary"
                  }
                >
                  {query.data!.data.portfolio.recruiting_status}
                </Badge>
                {query.data!.data.portfolio.recruiting_status === "stalled" && (
                  <ReopenButton portfolioId={portfolio_id!} />
                )}
                {query.data!.data.portfolio.recruiting_status ===
                  "recruiting" && (
                  <CloseRecruitingButton portfolioId={portfolio_id!} />
                )}
              </div>
              {query.data!.data.portfolio.recruiting_status === "stalled" && (
                <p className="text-sm text-destructive mb-2">
                  Stalled by creator consent withdrawals — the drop is greyed
                  out in the shop and can't be purchased until you reopen it.
                  You can assign replacement creators below (not advised
                  without their explicit buy-in).
                </p>
              )}
              <p className="text-sm opacity-70">
                {query.data!.data.portfolio.creator_count} creators · Composed{" "}
                {new Date(
                  query.data!.data.portfolio.created_at,
                ).toLocaleString()}
                {query.data!.data.portfolio.theme
                  ? ` · ${query.data!.data.portfolio.theme}`
                  : ""}
              </p>
              {/* Tier odds, stated ONCE for the dossier. They apply
                  identically to every creator on the roster below — which
                  is why they live up here and not on a roster row. */}
              <p className="mt-2 flex items-center gap-2 flex-wrap text-sm">
                <span className="opacity-70">Certificate tier odds:</span>
                <TierDistributionPills
                  distribution={
                    query.data!.data.portfolio.tier_distribution ?? null
                  }
                />
              </p>
            </header>

            <section className="mb-10">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-xl font-heading font-bold">Roster</h2>
                {["recruiting", "stalled"].includes(
                  query.data!.data.portfolio.recruiting_status,
                ) && (
                  <AssignCreatorPanel
                    portfolioId={portfolio_id!}
                    rosterUserIds={query.data!.data.creators.map(
                      (c) => c.creator_user_id,
                    )}
                  />
                )}
              </div>
              {/* Counsel ruling D3 — equal odds. Stated once for the whole
                  roster rather than repeated per row: there is no
                  per-creator knob of ANY kind to display, so a per-row
                  readout could only imply one creator differs from
                  another. Certificate-TIER odds are in the header — also
                  once, also for the whole dossier. */}
              <p className="mb-4 text-sm opacity-70">
                Equal odds — every creator in this portfolio is equally
                likely to be revealed, and the tier odds above apply the
                same way whichever one you get.
              </p>
              <div className="grid gap-2">
                {query.data!.data.creators.map((c) => (
                  <CreatorRosterRow key={c.portfolio_creator_id} row={c} />
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading font-bold mb-4">Release</h2>
              {dropQuery.isPending ? (
                <Skeleton className="h-48 w-full rounded-lg" />
              ) : dropQuery.isError ? (
                <Card>
                  <CardContent className="p-6">
                    <p className="text-destructive">
                      Couldn&apos;t load drop state: {dropQuery.error.message}
                    </p>
                  </CardContent>
                </Card>
              ) : dropQuery.data!.data === null ? (
                <DropReleasePanel
                  portfolioId={portfolio_id!}
                  portfolioKind={
                    query.data!.data.portfolio.kind as DropKind
                  }
                />
              ) : (
                <DropSummaryPanel drop={dropQuery.data!.data} />
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}

// =============================================================================
// Reopen + direct-assign (consent/stall management)
// =============================================================================

/** recruiting → composed. Server enforces the min-creators floor over
 *  NON-withdrawn roster rows; success unlocks the drop-release panel. */
function CloseRecruitingButton({ portfolioId }: { portfolioId: string }) {
  const close = useCloseRecruiting();
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={close.isPending}
      onClick={() =>
        close.mutate(
          { portfolio_id: portfolioId },
          {
            onSuccess: (outcome) =>
              toast.success(
                `Recruiting closed — composed with ${outcome.creator_count} creators. You can release the drop below.`,
              ),
          },
        )
      }
    >
      {close.isPending ? "Closing…" : "Close recruiting"}
    </Button>
  );
}

function ReopenButton({ portfolioId }: { portfolioId: string }) {
  const reopen = useReopenPortfolio();
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={reopen.isPending}
      onClick={() =>
        reopen.mutate(
          { portfolio_id: portfolioId },
          {
            onSuccess: (outcome) =>
              toast.success(
                `Portfolio reopened (${outcome.recruiting_status}).`,
              ),
          },
        )
      }
    >
      {reopen.isPending ? "Reopening…" : "Reopen portfolio"}
    </Button>
  );
}

/** Collapsible "Assign creator" picker — direct roster insert without an
 *  application. The creator gets a bell notification; their auto-assign
 *  opt-out (or a prior consent withdrawal) blocks the assign server-side. */
function AssignCreatorPanel({
  portfolioId,
  rosterUserIds,
}: {
  portfolioId: string;
  rosterUserIds: string[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const creators = useAdminApprovedCreators();
  const assign = useAssignCreatorToPortfolio();

  const candidates = (creators.data?.data ?? []).filter((c) => {
    if (rosterUserIds.includes(c.user_id)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.display_name ?? "").toLowerCase().includes(q) ||
      (c.handle ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="relative">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? "Close" : "Assign creator"}
      </Button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-brand-forest-200 bg-brand-paper-50 p-3 shadow-lg">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search approved creators…"
            className="mb-2 w-full rounded border border-brand-forest-200 bg-white px-2 py-1.5 text-sm"
            aria-label="Search approved creators"
          />
          <div className="max-h-64 overflow-y-auto">
            {creators.isPending ? (
              <p className="py-3 text-center text-sm opacity-60">Loading…</p>
            ) : candidates.length === 0 ? (
              <p className="py-3 text-center text-sm opacity-60">
                No assignable creators found.
              </p>
            ) : (
              candidates.map((c) => (
                <div
                  key={c.user_id}
                  className="flex items-center justify-between gap-2 border-b border-brand-forest-100 py-2 last:border-b-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {c.display_name ?? c.handle ?? c.user_id.slice(0, 8)}
                    </p>
                    {c.handle && (
                      <p className="truncate text-xs opacity-60">@{c.handle}</p>
                    )}
                  </div>
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={assign.isPending}
                    onClick={() =>
                      assign.mutate(
                        {
                          portfolio_id: portfolioId,
                          creator_user_id: c.user_id,
                        },
                        {
                          onSuccess: (outcome) =>
                            toast.success(
                              outcome.added
                                ? `${outcome.display_name ?? "Creator"} added to the roster (they've been notified).`
                                : "Already on the roster.",
                            ),
                        },
                      )
                    }
                  >
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreatorRosterRow({ row }: { row: AdminPortfolioCreatorRow }) {
  const withdrawn = row.withdrawn_at !== null;
  return (
    <Card className={withdrawn ? "opacity-60" : undefined}>
      <CardContent className="py-3 px-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="font-medium">
            {row.display_name?.trim() ||
              row.handle ||
              row.creator_user_id.slice(0, 8)}
            {withdrawn && (
              <Badge variant="destructive" className="ml-2 text-xs">
                consent withdrawn
              </Badge>
            )}
          </p>
          {row.handle ? (
            <p className="text-xs">@{row.handle}</p>
          ) : null}
          {row.optional_drop_specific_hello_text ? (
            <blockquote className="opacity-80 border-l-2 border-brand-forest-300 pl-3 mt-2 text-sm">
              {row.optional_drop_specific_hello_text}
            </blockquote>
          ) : null}
        </div>
        {/* No odds on a roster row. Neither which-creator (uniform) nor
            which-tier (a property of the dossier, printed once in the
            header): a per-row readout could only ever imply one creator
            differs from another. Position is roster order, not weight. */}
        <div className="flex items-center gap-3 text-sm">
          <span className="font-mono text-xs">
            #{row.position_in_portfolio}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Certificate-tier odds for the WHOLE dossier — one instance per page,
 *  in the header, never inside the roster. Null means the portfolio's
 *  tier_distribution column is NULL and the platform default applies. */
function TierDistributionPills({
  distribution,
}: {
  distribution: TierDistribution | null;
}) {
  if (!distribution) {
    return (
      <span className="flex items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          platform defaults
        </Badge>
        <span className="opacity-70">
          this dossier didn&apos;t set its own tier odds
        </span>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 flex-wrap font-mono">
      <span>
        <span className="opacity-70">c:</span>{" "}
        {(distribution.common * 100).toFixed(1)}%
      </span>
      <span>
        <span className="opacity-70">p:</span>{" "}
        {(distribution.preferred * 100).toFixed(1)}%
      </span>
      <span>
        <span className="opacity-70">prem:</span>{" "}
        {(distribution.premium * 100).toFixed(1)}%
      </span>
      <span>
        <span className="opacity-70">f:</span>{" "}
        {(distribution.founders * 100).toFixed(1)}%
      </span>
    </span>
  );
}

// =============================================================================
// Story 2.3 — Release panel (admin can release the portfolio as a drop)
// =============================================================================

function DropReleasePanel({
  portfolioId,
  portfolioKind,
}: {
  portfolioId: string;
  portfolioKind: DropKind;
}) {
  const releaseMut = useAdminCreateDrop();
  const [state, setState] = useState<DropReleaseFormState>(
    EMPTY_RELEASE_FORM_STATE,
  );
  const valid = isReleaseFormValid(state, portfolioKind);

  async function handleRelease() {
    const priceCents =
      portfolioKind === "welcome"
        ? 0
        : parseInt(state.price_cents_input, 10);
    const perUserCap =
      portfolioKind === "special"
        ? parseInt(state.per_user_cap_input, 10)
        : null;
    const scheduledAtIso = state.release_now
      ? new Date().toISOString()
      : parseDatetimeLocalToIso(state.scheduled_at_input);
    const retiringAtIso =
      portfolioKind === "welcome"
        ? null
        : state.retiring_at_input
          ? parseDatetimeLocalToIso(state.retiring_at_input)
          : null;
    if (!scheduledAtIso) {
      toast.error("Pick a valid scheduled time.");
      return;
    }

    const request: ReleaseDropRequest = {
      portfolio_id: portfolioId,
      kind: portfolioKind,
      price_cents: priceCents,
      per_user_cap: perUserCap,
      scheduled_at: scheduledAtIso,
      retiring_at: retiringAtIso,
      release_now: state.release_now,
      linked_bulletin_post_id: state.link_bulletin
        ? state.linked_bulletin_post_id
        : null,
    };

    try {
      const result = await releaseMut.mutateAsync(request);
      toast.success(`Drop released (${result.data.status}).`);
    } catch (err) {
      if (err instanceof AdminDropReleaseError) {
        const msg =
          err.code === "DROP_PORTFOLIO_NOT_COMPOSED"
            ? "Portfolio not in 'composed' state."
            : err.code === "DROP_KIND_MISMATCH_PORTFOLIO_KIND"
              ? `Drop kind '${err.dropKind}' doesn't match portfolio kind '${err.portfolioKind}'.`
              : err.code === "DROP_ALREADY_EXISTS_FOR_PORTFOLIO"
                ? "A drop already exists for this portfolio. Refresh the page."
                : err.code === "DROP_PER_USER_CAP_INVALID"
                  ? `per_user_cap invalid for ${err.kindEcho ?? portfolioKind} drop.`
                  : err.code === "DROP_INVALID_TIMESTAMP"
                    ? `Timestamp invalid: ${err.reason ?? "see field"}.`
                    : err.code === "DROP_BULLETIN_POST_NOT_FOUND"
                      ? "Linked Bulletin post not found."
                      : err.code === "DROP_BULLETIN_POST_NOT_PUBLISHED"
                        ? "Linked Bulletin post is still a draft — publish it first."
                        : err.code === "DROP_BULLETIN_POST_ALREADY_LINKED"
                          ? "That Bulletin post is already linked to another drop."
                          : err.code === "VALIDATION_FAILED"
                            ? err.field
                              ? `Invalid ${err.field}: ${err.message}`
                              : err.message
                            : `Release failed: ${err.message}`;
        toast.error(msg);
      } else {
        toast.error(`Release failed: ${(err as Error).message}`);
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Release as drop</CardTitle>
        <CardDescription>
          Configure pricing + release timing + (optionally) a linked
          Bulletin announcement, then publish the drop.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <DropReleaseForm
          portfolioKind={portfolioKind}
          mode="detail-panel"
          state={state}
          setState={setState}
        />
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="primary"
            disabled={!valid || releaseMut.isPending}
            onClick={handleRelease}
          >
            {releaseMut.isPending ? "Releasing…" : "Release drop"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DropSummaryPanel({ drop }: { drop: AdminDropDetail }) {
  const statusBadgeVariant =
    drop.status === "live"
      ? "success"
      : drop.status === "scheduled"
        ? "warning"
        : drop.status === "retiring"
          ? "warning"
          : "secondary";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Drop released</CardTitle>
        <CardDescription>
          Drop&nbsp;
          <span className="font-mono text-xs">
            {drop.drop_id.slice(0, 8)}
          </span>
          &nbsp;— created{" "}
          {new Date(drop.created_at).toLocaleString()}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={statusBadgeVariant}>{drop.status}</Badge>
          <Badge variant="secondary">{drop.kind}</Badge>
          <Badge variant="secondary" className="font-mono">
            {formatEur(drop.price_cents)}
          </Badge>
          {drop.per_user_cap !== null ? (
            <Badge variant="secondary">cap: {drop.per_user_cap}/user</Badge>
          ) : null}
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs uppercase opacity-60">Scheduled</dt>
            <dd>{new Date(drop.scheduled_at).toLocaleString()}</dd>
          </div>
          {drop.live_at ? (
            <div>
              <dt className="text-xs uppercase opacity-60">Live since</dt>
              <dd>{new Date(drop.live_at).toLocaleString()}</dd>
            </div>
          ) : null}
          {drop.retiring_at ? (
            <div>
              <dt className="text-xs uppercase opacity-60">Retires</dt>
              <dd>{new Date(drop.retiring_at).toLocaleString()}</dd>
            </div>
          ) : null}
          {drop.retired_at ? (
            <div>
              <dt className="text-xs uppercase opacity-60">Retired at</dt>
              <dd>{new Date(drop.retired_at).toLocaleString()}</dd>
            </div>
          ) : null}
        </dl>
        <div className="text-sm">
          <p className="text-xs uppercase opacity-60 mb-1">
            Bulletin announcement
          </p>
          {drop.linked_bulletin_post_id ? (
            <p>
              <Link
                to={`/admin/bulletin/${drop.linked_bulletin_post_id}`}
                className="underline decoration-dotted"
              >
                {drop.linked_bulletin_title ?? drop.linked_bulletin_slug}
              </Link>
            </p>
          ) : (
            <p className="opacity-70">No bulletin linked.</p>
          )}
        </div>
        <div className="text-xs opacity-60 pt-2 border-t border-brand-forest-100 flex gap-4">
          <Link
            to={`/admin/audit-log?event_kind=drop.created&subject_id=${drop.drop_id}`}
            className="underline decoration-dotted"
          >
            View audit log entries →
          </Link>
          <Link
            to={`/admin/drops?status=${drop.status}`}
            className="underline decoration-dotted"
          >
            Manage drop state →
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
